import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    Alert,
    AlertHandle,
    autoinject,
    bind,
    FormStore,
    inject,
    Modal,
    ModalHandle,
    NothingHere,
    TasksStore,
} from 'Mri.Web.Common';
import * as React from 'react';
import { FormGroup } from 'reactstrap';
import { AppStore } from '../../../Core/AppStore';
import { FavoriteButton } from '../../../Core/FavoriteButton';
import { ActiveDisplay } from '../../ActiveDisplay';
import { AdvancedDisplaySettingsForm } from '../DisplayForms/AdvancedDisplaySettingsForm';
import { DisplaysStore } from '../../DisplaysStore';
import { BsGearFill } from '@react-icons/all-files/bs/BsGearFill';
import { BsPencil } from '@react-icons/all-files/bs/BsPencil';

export interface IAdvancedSettingsModalProps {
    readonly display: ActiveDisplay;
    readonly modalHandle: ModalHandle;
}

@autoinject
@observer
export class AdvancedSettingsModal extends React.Component<IAdvancedSettingsModalProps> {
    public render() {
        return (
            <Modal
                title={this.title}
                modalHandle={this.props.modalHandle}
                footer={this.footer}
                size="md"
                className="my-2"
                onOpened={this.loadAsync}
            >
                <Alert alertHandle={this.alertHandle} />

                <div className="container">
                    {this.advancedSettingsStore && (
                        <AdvancedDisplaySettingsForm advancedSettingsStore={this.advancedSettingsStore} />
                    )}

                    <NothingHere
                        empty={!this.advancedSettingsStore}
                        icon={<BsPencil />}
                        loading={this.loading}
                        loadingMessage="Loading Configuration..."
                    />
                </div>
            </Modal>
        );
    }

    @computed
    private get title() {
        return (
            <div className="w-100 text-center">
                <span className="fw-bold">
                    <BsGearFill /> Advanced Settings: {this.props.display.serialNumber}
                </span>
            </div>
        );
    }

    @computed
    private get footer() {
        return (
            <div className="w-100 d-flex justify-content-around">
                <div className="w-100 d-flex justify-content-around">
                    <button className="btn btn-secondary" onClick={this.props.modalHandle.close}>
                        Cancel
                    </button>

                    <FormGroup className="mb-0">
                        <div className="col-md-offset-2">
                            <button type="submit" className="btn btn-primary" onClick={this.submitAsync}>
                                {this.loading ? 'Updating...' : 'Update'}
                            </button>
                        </div>
                    </FormGroup>
                </div>
            </div>
        );
    }

    @bind
    @action
    private async loadAsync() {
        this.loading = true;

        try {
            await this.displaysStore.loadSmartVuSettingsAsync(this.props.display.serialNumber);
            const settings = this.displaysStore.smartVuSettings.get(this.props.display.serialNumber);

            if (settings) this.advancedSettingsStore = new FormStore<Mri.Displays.DisplaySmartVuSettings>(settings);
            else
                this.advancedSettingsStore = new FormStore<Mri.Displays.DisplaySmartVuSettings>({
                    serialNumber: this.props.display.serialNumber,
                    disableAutomaticallyManagedIp: false,
                    disableGpsGeocoding: false,
                    disableJobScheduler: false,
                    disableOnDemandJobs: false,
                    disableProxyMriPrivateLookup: false,
                });
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async submitAsync(event: React.FormEvent<HTMLButtonElement>) {
        event.preventDefault();
        if (!this.advancedSettingsStore) {
            console.error('SettingsStore is not defined');
            this.appStore.setError(new Error('SettingsStore is not defined'));
            return;
        }

        this.advancedSettingsStore.onSubmit();
        if (this.advancedSettingsStore.validate()) {
            const task = this.tasksStore.startSessionTask('Update Settings For' + this.props.display.serialNumber);

            try {
                await this.displaysStore.updateSmartVuSettingsAsync(
                    this.props.display.serialNumber,
                    this.advancedSettingsStore.values
                );

                task.complete();
                this.props.modalHandle.close();
            } catch (e: any) {
                if (e.modelState) {
                    this.advancedSettingsStore.clearGlobalErrors();
                    this.advancedSettingsStore.addErrors(e.modelState);
                } else {
                    task.error(e);
                }
            }
        } else {
            console.log('invalid');
        }
    }

    @observable
    private advancedSettingsStore?: FormStore<Mri.Displays.DisplaySmartVuSettings>;
    @observable
    private loading = false;
    private alertHandle = new AlertHandle();
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private tasksStore!: TasksStore;
}
